import React, { useEffect, useMemo, useState } from 'react';

import { Column, Line } from '@ant-design/plots';
import { Button, Card, Col, DatePicker, Empty, Row, Select, Space, Spin, Table, Typography, message } from 'antd';
import moment from 'moment-timezone';

import useDebounce from '~/hooks/useDebounce';
import api from '~/services/api';

import { Container, FiltersCard, KpiCard, KpiGrid, KpiValue, SectionTitle } from './styles';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_FILTERS = {
  dateMode: 'range',
  range: '30',
  from: null,
  to: null,
  storeIds: [],
  carrierCodes: [],
  categoryIds: [],
  partNumbers: [],
  identifiers: [],
  lateTop: 10,
};

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last 1 year' },
  { value: '730', label: 'Last 2 years' },
];

const LATE_TOP_OPTIONS = [10, 25, 50, 100].map((value) => ({ value, label: `Top ${value}` }));
const DEBOUNCE_MS = 300;
const EMPTY_FILTER_OPTIONS = {
  stores: [],
  carriers: [],
  categories: [],
  partNumbers: [],
  identifiers: [],
};

const CARRIER_GROUP_TO_CODES = {
  amazon: ['amazon buy shipping'],
  purolator: ['purolator canada', 'purolator_ca'],
  paquete_express: ['paquete express'],
  canada_post: ['canada_post'],
  ups: ['ups'],
  dhl: ['dhl express canada', 'dhl_express_canada', 'dhl express'],
  usps: ['usps'],
  fedex: ['fedex'],
  other: ['other'],
};

const CARRIER_GROUP_LABELS = {
  amazon: 'Amazon Buy Shipping',
  purolator: 'Purolator',
  paquete_express: 'Paquete Express',
  canada_post: 'Canada Post',
  ups: 'UPS',
  dhl: 'DHL',
  usps: 'USPS',
  fedex: 'FedEx',
  other: 'Other',
};

function safeNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function lateRateFromPercent(percent) {
  return Math.max(0, 100 - safeNumber(percent));
}

function kpiClass(rate) {
  if (rate >= 90) return 'good';
  if (rate >= 80) return 'warn';
  return 'bad';
}

function normalizeCarrierCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function toTitleCase(value) {
  return String(value || '')
    .split(' ')
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');
}

function carrierGroupFromCode(code) {
  const normalized = normalizeCarrierCode(code);

  if (normalized.includes('dhl')) return 'dhl';
  if (normalized.includes('fedex')) return 'fedex';
  if (normalized.includes('ups')) return 'ups';
  if (normalized.includes('usps')) return 'usps';
  if (normalized.includes('purolator')) return 'purolator';
  if (normalized.includes('canada post')) return 'canada_post';
  if (normalized.includes('amazon buy shipping')) return 'amazon';
  if (normalized.includes('paquete express')) return 'paquete_express';
  if (normalized === 'other') return 'other';

  return normalized.replace(/\s+/g, '_');
}

function carrierLabelFromCode(code) {
  const group = carrierGroupFromCode(code);
  return CARRIER_GROUP_LABELS[group] || toTitleCase(normalizeCarrierCode(code));
}

function buildQueryParams(filters) {
  const params = {
    lateTop: filters.lateTop,
  };

  if (filters.dateMode === 'range' && filters.range) {
    params.range = filters.range;
  }

  if (filters.dateMode === 'custom' && filters.from && filters.to) {
    params.from = filters.from;
    params.to = filters.to;
  }

  if (filters.storeIds.length) params.storeIds = filters.storeIds.join(',');
  if (filters.carrierCodes.length) {
    const expandedCarrierCodes = [...new Set(filters.carrierCodes.flatMap((group) => CARRIER_GROUP_TO_CODES[group] || [group]))];
    params.carrierCodes = expandedCarrierCodes.join(',');
  }
  if (filters.categoryIds.length) params.categoryIds = filters.categoryIds.join(',');
  if (filters.partNumbers.length) params.partNumbers = filters.partNumbers.join(',');
  if (filters.identifiers.length) params.identifiers = filters.identifiers.join(',');

  return params;
}

function toPercent(value) {
  return `${safeNumber(value).toFixed(1)}%`;
}

function getRelativeYAxisMax(data) {
  if (!Array.isArray(data) || !data.length) return 10;
  const maxValue = data.reduce((max, row) => Math.max(max, safeNumber(row.value)), 0);
  return Math.max(10, Math.ceil(maxValue / 10) * 10);
}

function mergeFilterOptions(previous, incoming) {
  const prevStores = previous.stores || [];
  const nextStores = incoming.stores || [];
  const mergedStoresMap = [...prevStores, ...nextStores].reduce((acc, row) => {
    if (row?.storeId != null) {
      acc[String(row.storeId)] = row;
    }
    return acc;
  }, {});

  const prevCategories = previous.categories || [];
  const nextCategories = incoming.categories || [];
  const mergedCategoriesMap = [...prevCategories, ...nextCategories].reduce((acc, row) => {
    if (row?.categoryId != null) {
      acc[String(row.categoryId)] = row;
    }
    return acc;
  }, {});

  const mergeStringArray = (a = [], b = []) => [...new Set([...a, ...b])];

  return {
    stores: Object.values(mergedStoresMap),
    carriers: mergeStringArray(previous.carriers, incoming.carriers),
    categories: Object.values(mergedCategoriesMap),
    partNumbers: mergeStringArray(previous.partNumbers, incoming.partNumbers),
    identifiers: mergeStringArray(previous.identifiers, incoming.identifiers),
  };
}

export function OrderOnTime() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [report, setReport] = useState({
    filters: {},
    summary: { onTime: 0, late: 0, total: 0, percent: 0 },
    byPeriod: [],
    byStore: [],
    byCarrier: [],
    byCategory: [],
    lateOrders: [],
    filterOptions: {
      stores: [],
      carriers: [],
      categories: [],
      partNumbers: [],
      identifiers: [],
    },
  });
  const [loading, setLoading] = useState(false);
  const [filterOptionPool, setFilterOptionPool] = useState(EMPTY_FILTER_OPTIONS);
  const [openSelects, setOpenSelects] = useState({
    stores: false,
    carriers: false,
    categories: false,
    partNumbers: false,
    identifiers: false,
  });

  const debouncedFilters = useDebounce(filters, DEBOUNCE_MS);

  useEffect(() => {
    async function fetchReport() {
      setLoading(true);
      try {
        const response = await api.get('kpi/OrdersOnTimeYearly', {
          params: buildQueryParams(debouncedFilters),
        });
        const nextReport = response.data || {};
        setReport(nextReport);
        setFilterOptionPool((prev) => mergeFilterOptions(prev, nextReport.filterOptions || EMPTY_FILTER_OPTIONS));
      } catch (error) {
        message.error('Unable to load Orders On Time KPI data.');
      } finally {
        setLoading(false);
      }
    }

    fetchReport();
  }, [debouncedFilters]); // eslint-disable-line

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleDateModeChange(mode) {
    setFilters((prev) => ({
      ...prev,
      dateMode: mode,
      ...(mode === 'range' ? { from: null, to: null } : { range: '' }),
    }));
  }

  function handleCustomDateChange(dates) {
    if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
      setFilters((prev) => ({ ...prev, from: null, to: null }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      dateMode: 'custom',
      range: '',
      from: dates[0].format('YYYY-MM-DD'),
      to: dates[1].format('YYYY-MM-DD'),
    }));
  }

  function resetFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  const summary = report.summary || { onTime: 0, late: 0, total: 0, percent: 0 };
  const byPeriod = report.byPeriod || [];
  const byStore = report.byStore || [];
  const byCarrier = report.byCarrier || [];
  const byCategory = report.byCategory || [];
  const lateOrders = report.lateOrders || [];
  const filterOptions = filterOptionPool;

  const avgDaysLate = useMemo(() => {
    if (!lateOrders.length) return 0;
    const total = lateOrders.reduce((sum, row) => sum + safeNumber(row.daysLate), 0);
    return total / lateOrders.length;
  }, [lateOrders]);

  const trendData = useMemo(
    () =>
      byPeriod.flatMap((row) => [
        {
          periodLabel: row.periodLabel,
          metric: 'On-time %',
          value: safeNumber(row.percent),
        },
        {
          periodLabel: row.periodLabel,
          metric: 'Late %',
          value: lateRateFromPercent(row.percent),
        },
      ]),
    [byPeriod]
  );

  const byCarrierChartData = useMemo(() => {
    const grouped = byCarrier.reduce((acc, row) => {
      const group = carrierGroupFromCode(row.carrierCode);
      const current = acc[group] || {
        onTime: 0,
        late: 0,
        total: 0,
        label: carrierLabelFromCode(row.carrierCode),
      };

      current.onTime += safeNumber(row.onTime);
      current.late += safeNumber(row.late);
      current.total += safeNumber(row.total);

      acc[group] = current;
      return acc;
    }, {});

    return Object.values(grouped).map((row) => ({
      name: row.label,
      value: row.late,
      lateOrders: row.late,
      onTimeOrders: row.onTime,
      totalOrders: row.total,
      lateRate: row.total ? (row.late / row.total) * 100 : 0,
    }));
  }, [byCarrier]);

  const byCategoryChartData = useMemo(
    () =>
      byCategory.map((row) => ({
        name: row.categoryName || 'Unknown',
        value: safeNumber(row.late),
        lateOrders: safeNumber(row.late),
        onTimeOrders: safeNumber(row.onTime),
        totalOrders: safeNumber(row.total),
        lateRate: lateRateFromPercent(row.percent),
      })),
    [byCategory]
  );

  const byStoreChartData = useMemo(
    () =>
      byStore.map((row) => ({
        name: row.storeName || 'Unassigned',
        value: safeNumber(row.late),
        lateOrders: safeNumber(row.late),
        onTimeOrders: safeNumber(row.onTime),
        totalOrders: safeNumber(row.total),
        lateRate: lateRateFromPercent(row.percent),
      })),
    [byStore]
  );

  const trendConfig = {
    data: trendData,
    xField: 'periodLabel',
    yField: 'value',
    seriesField: 'metric',
    smooth: true,
    color: ({ metric }) => (metric === 'On-time %' ? '#639922' : '#E24B4A'),
    yAxis: {
      min: 0,
      max: 100,
      label: {
        formatter: (v) => `${v}%`,
      },
    },
    xAxis: {
      label: {
        autoRotate: true,
      },
    },
    legend: {
      position: 'top',
    },
    tooltip: {
      formatter: (datum) => ({ name: datum.metric, value: `${safeNumber(datum.value).toFixed(1)}%` }),
    },
    point: {
      size: 3,
      shape: 'circle',
    },
    height: 280,
  };

  const breakdownChartConfig = (data) => {
    const yMax = getRelativeYAxisMax(data);

    return {
      data,
      xField: 'name',
      yField: 'value',
      columnWidthRatio: 0.7,
      color: ({ value }) => {
        if (value > 20) return '#E24B4A';
        if (value > 10) return '#EF9F27';
        return '#3B82F6';
      },
      yAxis: {
        min: 0,
        max: yMax,
      },
      xAxis: {
        label: {
          autoHide: true,
          autoRotate: true,
        },
      },
      label: {
        position: 'middle',
        style: {
          fill: '#fff',
        },
        formatter: (datum) => `${safeNumber(datum.value)}`,
      },
      tooltip: {
        customContent: (_title, items) => {
          const datum = items?.[0]?.data || {};
          const lateOrders = safeNumber(datum.lateOrders, safeNumber(datum.value));
          const onTimeOrders = safeNumber(datum.onTimeOrders);
          const totalOrders = safeNumber(datum.totalOrders, lateOrders + onTimeOrders);
          const lateRate = totalOrders ? (lateOrders / totalOrders) * 100 : safeNumber(datum.lateRate);

          return `
            <div style="padding:8px 10px;min-width:190px">
              <div style="font-weight:600;margin-bottom:6px">${datum.name || ''}</div>
              <div>Late Orders: <b>${lateOrders}</b></div>
              <div>On-time Orders: <b>${onTimeOrders}</b></div>
              <div>Total Orders: <b>${totalOrders}</b></div>
              <div>Late Rate: <b>${lateRate.toFixed(1)}%</b></div>
            </div>
          `;
        },
      },
      height: 260,
    };
  };

  const lateOrdersColumns = [
    {
      title: 'Order #',
      dataIndex: 'orderNum',
      key: 'orderNum',
      width: 120,
      render: (value) => <Text strong>{value || '-'}</Text>,
    },
    {
      title: 'Store',
      dataIndex: 'storeName',
      key: 'storeName',
      render: (value) => value || 'Unassigned',
    },
    {
      title: 'Carrier',
      dataIndex: 'carrierCode',
      key: 'carrierCode',
      render: (value) => (value ? carrierLabelFromCode(value) : '-'),
      width: 100,
    },
    {
      title: 'Days Late',
      dataIndex: 'daysLate',
      key: 'daysLate',
      width: 110,
      render: (value) => <Text type="danger">+{safeNumber(value)}d</Text>,
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      key: 'categories',
      render: (value) => (Array.isArray(value) && value.length ? value.join(', ') : '-'),
    },
    {
      title: 'Part Numbers',
      dataIndex: 'partNumbers',
      key: 'partNumbers',
      render: (value) => (Array.isArray(value) && value.length ? value.join(', ') : '-'),
    },
    {
      title: 'Identifiers',
      dataIndex: 'identifiers',
      key: 'identifiers',
      render: (value) => {
        if (!Array.isArray(value) || !value.length) return '-';
        const cleaned = value.map((item) => String(item || '').trim()).filter(Boolean);
        return cleaned.length ? cleaned.join(', ') : '-';
      },
    },
  ];

  const storeOptions = (filterOptions.stores || []).map((store) => ({
    value: String(store.storeId),
    label: store.storeName,
  }));

  const carrierOptions = useMemo(() => {
    const grouped = (filterOptions.carriers || []).reduce((acc, carrier) => {
      const group = carrierGroupFromCode(carrier);
      if (!acc[group]) {
        acc[group] = {
          value: group,
          label: carrierLabelFromCode(carrier),
        };
      }
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.carriers]);

  const categoryOptions = (filterOptions.categories || []).map((category) => ({
    value: String(category.categoryId),
    label: category.categoryName,
  }));

  const partNumberOptions = (filterOptions.partNumbers || []).map((value) => ({ value, label: value }));
  const identifierOptions = (filterOptions.identifiers || []).map((value) => ({ value, label: value }));

  const dateRangeValue =
    filters.dateMode === 'custom' && filters.from && filters.to ? [moment(filters.from, 'YYYY-MM-DD'), moment(filters.to, 'YYYY-MM-DD')] : null;

  function handleDropdownVisibleChange(key, isOpen) {
    setOpenSelects((prev) => ({ ...prev, [key]: isOpen }));
  }

  function keepDropdownOpen(key) {
    setOpenSelects((prev) => ({ ...prev, [key]: true }));
  }

  return (
    <Container>
      <Title level={3}>Orders On Time KPI</Title>

      <FiltersCard title="Filters" size="small">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12} xl={6}>
            <Text strong>Date Mode</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={filters.dateMode}
              onChange={handleDateModeChange}
              options={[
                { value: 'range', label: 'Preset range' },
                { value: 'custom', label: 'Custom dates' },
              ]}
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Range</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }}
              value={filters.range || undefined}
              disabled={filters.dateMode !== 'range'}
              onChange={(value) => updateFilter('range', value)}
              options={RANGE_OPTIONS}
            />
          </Col>

          <Col xs={24} md={12} xl={12}>
            <Text strong>Custom Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              value={dateRangeValue}
              format="YYYY-MM-DD"
              disabled={filters.dateMode !== 'custom'}
              onChange={handleCustomDateChange}
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Stores</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All stores"
              value={filters.storeIds}
              options={storeOptions}
              onChange={(value) => updateFilter('storeIds', value)}
              onSelect={() => keepDropdownOpen('stores')}
              open={openSelects.stores}
              onDropdownVisibleChange={(isOpen) => handleDropdownVisibleChange('stores', isOpen)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Carriers</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All carriers"
              value={filters.carrierCodes}
              options={carrierOptions}
              onChange={(value) => updateFilter('carrierCodes', value)}
              onSelect={() => keepDropdownOpen('carriers')}
              open={openSelects.carriers}
              onDropdownVisibleChange={(isOpen) => handleDropdownVisibleChange('carriers', isOpen)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Categories</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All categories"
              value={filters.categoryIds}
              options={categoryOptions}
              onChange={(value) => updateFilter('categoryIds', value)}
              onSelect={() => keepDropdownOpen('categories')}
              open={openSelects.categories}
              onDropdownVisibleChange={(isOpen) => handleDropdownVisibleChange('categories', isOpen)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Part Numbers</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All part numbers"
              value={filters.partNumbers}
              options={partNumberOptions}
              onChange={(value) => updateFilter('partNumbers', value)}
              onSelect={() => keepDropdownOpen('partNumbers')}
              open={openSelects.partNumbers}
              onDropdownVisibleChange={(isOpen) => handleDropdownVisibleChange('partNumbers', isOpen)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>

          <Col xs={24} md={12} xl={6}>
            <Text strong>Identifiers</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="All identifiers"
              value={filters.identifiers}
              options={identifierOptions}
              onChange={(value) => updateFilter('identifiers', value)}
              onSelect={() => keepDropdownOpen('identifiers')}
              open={openSelects.identifiers}
              onDropdownVisibleChange={(isOpen) => handleDropdownVisibleChange('identifiers', isOpen)}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </Col>
        </Row>

        <div className="filter-actions">
          <Space>
            <Button onClick={resetFilters}>Reset Filters</Button>
          </Space>
        </div>
      </FiltersCard>

      <Spin spinning={loading}>
        <KpiGrid>
          <KpiCard>
            <span className="label">On-time rate</span>
            <KpiValue className={kpiClass(safeNumber(summary.percent))}>{toPercent(summary.percent)}</KpiValue>
            <span className="sub">Target: 90%</span>
          </KpiCard>
          <KpiCard>
            <span className="label">Total orders</span>
            <KpiValue>{safeNumber(summary.total)}</KpiValue>
            <span className="sub">In selected period</span>
          </KpiCard>
          <KpiCard>
            <span className="label">On time</span>
            <KpiValue className="good">{safeNumber(summary.onTime)}</KpiValue>
            <span className="sub">Orders shipped on time</span>
          </KpiCard>
          <KpiCard>
            <span className="label">Late orders</span>
            <KpiValue className="bad">{safeNumber(summary.late)}</KpiValue>
            <span className="sub">Orders past due</span>
          </KpiCard>
          <KpiCard>
            <span className="label">Avg. days late</span>
            <KpiValue className={avgDaysLate > 4 ? 'bad' : avgDaysLate > 2 ? 'warn' : 'good'}>{safeNumber(avgDaysLate).toFixed(1)}d</KpiValue>
            <span className="sub">Based on current late list</span>
          </KpiCard>
        </KpiGrid>

        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Card title="On-time shipment trend">
              {trendData.length ? <Line {...trendConfig} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No trend data" />}
            </Card>
          </Col>

          <Col span={24}>
            <Text type="secondary">These charts show the number of late orders for each group in the current filter selection.</Text>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Late orders by carrier">
              {byCarrierChartData.length ? (
                <Column {...breakdownChartConfig(byCarrierChartData)} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No carrier data" />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Late orders by category">
              {byCategoryChartData.length ? (
                <Column {...breakdownChartConfig(byCategoryChartData)} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No category data" />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Late orders by store">
              {byStoreChartData.length ? (
                <Column {...breakdownChartConfig(byStoreChartData)} />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No store data" />
              )}
            </Card>
          </Col>
        </Row>

        <SectionTitle level={5}>Late Order Details</SectionTitle>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <Space align="center">
            <Text strong>Late Orders</Text>
            <Select style={{ width: 130 }} value={filters.lateTop} options={LATE_TOP_OPTIONS} onChange={(value) => updateFilter('lateTop', value)} />
          </Space>
        </div>
        <Card bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={lateOrders}
            columns={lateOrdersColumns}
            rowKey={(record) => String(record.orderId)}
            pagination={false}
            locale={{ emptyText: 'No late orders in this selection' }}
            scroll={{ x: 1080 }}
          />
        </Card>
      </Spin>
    </Container>
  );
}
